import { supabase } from '@/integrations/supabase/client';
import { logTicketEventToOcis } from '@/utils/ocisIngest';
import { deriveMergeOperationId } from '@/lib/tickets/splitSurfaceIdentity';
// DTC-J: route the merge-flow `nowIso` write timestamp through the
// canonical clock so persisted lifecycle audit data is deterministic
// under `clock.setFixed(...)`. The L96 `toLocaleString` display
// timestamp is class-A display-only and intentionally preserved.
import { clock } from '@/utils/clock';
// Phase 10.1: partial-completion merge runtime preflight. Composes
// Phase 7.4 truth-preservation classifier + Phase 5.5 Due Out gate so
// silent reopen of completed work is rejected before any mutation.
import {
  getSplitMergeCompletionPlan,
  type SplitMergeCompletionPlan,
  type SplitMergePlanKind,
  type SplitPartShape,
} from '@/utils/partialCompletionMerge';
import type { SplitMergeBlocker } from '@/utils/dueOutSplitMergeValidation';

/**
 * Find all split siblings of a ticket
 * Returns tickets that share the same split_group_id
 */
export async function findSplitSiblings(ticketId: string): Promise<any[] | null> {
  try {
    // First, get the current ticket to find its split family
    const { data: currentTicket, error: currentError } = await supabase
      .from('tickets')
      .select('id, split_group_id, split_reason')
      .eq('id', ticketId)
      .single();

    if (currentError) throw currentError;
    if (!currentTicket) return null;

    // Check if this ticket has a split_group_id
    const splitGroupId = currentTicket.split_group_id;
    
    if (!splitGroupId) {
      // Not a split ticket
      return null;
    }

    // Find all siblings with the same split_group_id
    const { data: siblings, error: siblingsError } = await supabase
      .from('tickets')
      .select('*')
      .eq('split_group_id', splitGroupId)
      // DMC-D3b: canonical active-ticket filter — both reps excluded.
      // The sibling-merge path itself is the V-A-16 caller that
      // pre-DMC-D1 produced ghost rows (status='Deleted' AND
      // deleted_at IS NULL). Now that the primitive guarantees both
      // reps track each other, this query also skips deleted_at
      // ghosts as defense-in-depth — siblings already merged-back-
      // together stay correctly excluded from any subsequent merge.
      .neq('status', 'Deleted')
      .is('deleted_at', null)
      .order('split_sequence', { ascending: true });

    if (siblingsError) throw siblingsError;

    return siblings || [];
  } catch (error) {
    console.error('Error finding split siblings:', error);
    throw error;
  }
}

/**
 * Phase 10.1: structured error thrown by the partial-completion merge
 * preflight when the existing collect-all-jobs runtime path is unsafe.
 * Callers can inspect `code === 'PARTIAL_COMPLETION_MERGE_BLOCKED'` and
 * read `.plan` for the truth-preservation verdict.
 */
export interface PartialCompletionMergeBlockedError extends Error {
  code: 'PARTIAL_COMPLETION_MERGE_BLOCKED';
  plan: SplitMergeCompletionPlan;
}

/**
 * Phase 10.1: Due-Out re-evaluation blockers. The Phase 5.5 Due Out
 * gate requires a `mergedProjectedFinish` the existing merge runtime
 * cannot cheaply compute (no dry-run smart-assign exists). Phase 10.1
 * surfaces these blockers via `console.warn` and DOES NOT throw on
 * them. A future Phase 10.x will compute the projection and promote
 * these to throw-blockers. Truth-preservation blockers (silent
 * reopen / hide / running timer / picked up / missing tech / missing
 * audit) always throw regardless.
 */
const DUE_OUT_GATE_BLOCKERS: ReadonlyArray<SplitMergeBlocker> = Object.freeze([
  'missing_due_out',
  'due_out_risk_not_recalculated',
  'final_completion_after_due_out',
]);

/**
 * Phase 10.1 runtime preflight. Throws a
 * `PartialCompletionMergeBlockedError` when the proposed merge would
 * silently reopen completed work / hide remaining work / lose tech
 * attribution / merge a running timer or picked-up part / merge
 * without an audit user.
 *
 * Pure. Exported for unit-testability — the consumer (`mergeSplitTickets`)
 * passes already-fetched siblings + the auth user id.
 *
 * Tolerated for Phase 10.1 (warn-only): the Phase 5.5 Due Out gate
 * blockers (`missing_due_out` / `due_out_risk_not_recalculated` /
 * `final_completion_after_due_out`). Phase 10.x will compute
 * `mergedProjectedFinish` and promote these to throws.
 */
export function assertSafePartialCompletionMergeOrThrow(
  siblings: ReadonlyArray<SplitPartShape & { due_out?: string | Date | null }>,
  primaryTicket: SplitPartShape & { due_out?: string | Date | null },
  actorUserId: string | null | undefined,
): SplitMergeCompletionPlan {
  const plan = getSplitMergeCompletionPlan({
    parts: siblings,
    dueOut: primaryTicket?.due_out ?? null,
    mergedProjectedFinish: null,
    auditContext: {
      actorUserId: actorUserId ?? null,
      reason: 'manual_merge',
    },
  });

  const truthBlockers = plan.blockers.filter(
    b => !DUE_OUT_GATE_BLOCKERS.includes(b as SplitMergeBlocker),
  );
  const dueOutBlockers = plan.blockers.filter(b =>
    DUE_OUT_GATE_BLOCKERS.includes(b as SplitMergeBlocker),
  );

  const SAFE_KIND: SplitMergePlanKind = 'merge_incomplete_remaining_work_only';
  const DUE_OUT_DEFERRED_KIND: SplitMergePlanKind =
    'block_due_out_recalculation_required';

  // Phase 5.5's `due_out_risk_not_recalculated` blocker fires
  // unconditionally when `mergedProjectedFinish` is null, which makes
  // the plan-kind ladder collapse non-safe cases (completed+incomplete,
  // all-completed, unknown, running, terminal) into
  // `block_due_out_recalculation_required` before their narrower kinds
  // can resolve. Defend by reading the classification buckets directly:
  // any completed / picked_up / deleted / running / unknown part means
  // the existing collect-all-jobs runtime path is unsafe regardless of
  // which plan kind the ladder picked.
  const counts = plan.completionSummary.countsByState;
  const hasCompletedPart = counts.completed_part > 0;
  const hasPickedUpPart = counts.picked_up_part > 0;
  const hasDeletedPart = counts.deleted_part > 0;
  const hasRunningPart = counts.running_part > 0;
  const hasUnknownPart = counts.unknown_part > 0;
  const hasRolloverContinuationPart = counts.rollover_continuation_part > 0;
  const requiresNewRuntimePath =
    hasCompletedPart ||
    hasPickedUpPart ||
    hasDeletedPart ||
    hasRunningPart ||
    hasUnknownPart ||
    hasRolloverContinuationPart;

  // Existing runtime only safely supports the all-incomplete path. The
  // `block_due_out_recalculation_required` kind is tolerated IFF its
  // only fault is the Due Out re-eval requirement we explicitly defer.
  const kindIsSafe =
    !requiresNewRuntimePath &&
    (plan.kind === SAFE_KIND ||
      (plan.kind === DUE_OUT_DEFERRED_KIND && truthBlockers.length === 0));

  if (truthBlockers.length > 0 || !kindIsSafe) {
    const err = new Error(
      `[mergeSplitTickets] Phase 10.1 partial-completion merge preflight blocked: ` +
        `kind=${plan.kind}; truth_blockers=${
          truthBlockers.join(',') || '<none>'
        }; due_out_blockers=${dueOutBlockers.join(',') || '<none>'}; ` +
        `preserving completed split history requires the partial-completion ` +
        `merge runtime path (deferred). The existing collect-all-jobs path is ` +
        `unsafe for mixed-state split groups.`,
    ) as PartialCompletionMergeBlockedError;
    err.code = 'PARTIAL_COMPLETION_MERGE_BLOCKED';
    err.plan = plan;
    throw err;
  }

  if (dueOutBlockers.length > 0) {
    console.warn(
      `[mergeSplitTickets] Phase 10.1 Due Out re-evaluation deferred ` +
        `(blockers=${dueOutBlockers.join(',')}). Phase 10.x will add a ` +
        `projection-based Due Out gate.`,
    );
  }

  return plan;
}

/**
 * Merge split tickets back into a single ticket
 * Uses the edge function to handle the complex logic
 */
/**
 * 8G.2D.3.1 §6 — THE DURABLE FAMILY, AS DISTINCT FROM THE LIVE ONE.
 *
 * `findSplitSiblings` answers "who is live in this family right now", which is
 * what a preflight and a stale-state check need. It is NOT what recovery needs:
 * a committed merge leaves nobody live, so that question returns nothing
 * precisely when the operator most needs an answer.
 *
 * This asks the other question — "which family is this ticket's, ever" — and it
 * keeps working on a superseded row, because supersession does not clear
 * `split_group_id`.
 */
export async function resolveDurableSplitFamilyId(
  ticketId: string,
): Promise<string | null> {
  const { data, error } = await supabase
    .from('tickets')
    .select('split_group_id')
    .eq('id', ticketId)
    .maybeSingle();
  if (error) throw error;
  return (data?.split_group_id as string | undefined) ?? null;
}

export async function mergeSplitTickets(ticketId: string): Promise<string> {
  // ══ 1. THE DURABLE FAMILY FIRST ═════════════════════════════════════════
  // Before anything that a successful merge could have destroyed.
  const splitGroupId = await resolveDurableSplitFamilyId(ticketId);
  if (!splitGroupId) {
    throw new Error(MERGE_REFUSAL_COPY.NOT_A_SPLIT_FAMILY);
  }

  // ══ 2. ONE FAMILY, ONE DURABLE IDENTITY ═════════════════════════════════
  // Keyed on the group alone. 8G.2D.3 keyed it on the group plus the LIVE
  // sibling ids, which the merge itself supersedes — so a retry after a lost
  // response could not reproduce the id of the act it was retrying.
  const operationId = await deriveMergeOperationId(splitGroupId);

  // ══ 3. THE LIVE FAMILY, FOR THE THINGS THAT NEED IT ═════════════════════
  const siblings = await findSplitSiblings(ticketId);
  const liveSiblingIds = (siblings ?? []).map((s: any) => s.id as string);
  const isRecoveryAttempt = liveSiblingIds.length < 2;

  if (!isRecoveryAttempt) {
    // A NEW merge: the product's supported-domain gate runs, and the live set
    // is sent so the authority can refuse a family that changed underneath the
    // operator. Everything here is revalidated server-side under the locks.
    const primaryTicket = siblings![0];
    const { data: { user: preflightUser } } = await supabase.auth.getUser();
    assertSafePartialCompletionMergeOrThrow(
      siblings as unknown as ReadonlyArray<SplitPartShape & { due_out?: string | Date | null }>,
      primaryTicket as unknown as SplitPartShape & { due_out?: string | Date | null },
      preflightUser?.id ?? null,
    );
  }
  // If this IS a recovery attempt we do NOT refuse here. The authority checks
  // `structural_merge_operations` BEFORE it judges mergeability, so it is the
  // only thing that can tell "already done" from "never possible" — and asking
  // it costs nothing, because a request with no matching durable operation is
  // still refused truthfully on the far side.

  const { data: { user } } = await supabase.auth.getUser();

  // ══ 4. ONE CALL. ONE TRANSACTION. ═══════════════════════════════════════
  const invoke = () =>
    (supabase as any).rpc('execute_structural_merge_atomic', {
      p_source_ticket_id: ticketId,
      p_operation_id: operationId,
      // Only a NEW merge has a live family to be stale about.
      p_expected_sibling_ids: isRecoveryAttempt ? null : liveSiblingIds,
      p_target: null,
    });

  let data: any;
  let error: any;
  try {
    ({ data, error } = await invoke());
  } catch (transportError) {
    // ══ §8 — THE ANSWER WAS LOST, NOT THE ACT ═════════════════════════════
    // A transport failure says nothing about whether the transaction
    // committed. Telling the operator "merge failed" when the database says
    // otherwise is the lie this exists to prevent. The SAME operation id is
    // presented again exactly once: if the act committed, the authority
    // answers ALREADY_APPLIED; if it did not, this is simply the first
    // attempt arriving late. No new identity, and no retry loop.
    try {
      ({ data, error } = await invoke());
    } catch {
      throw transportError;
    }
  }

  if (error) throw error;

  const outcome = data?.outcome as string | undefined;
  const mergedTicketId = data?.merged_ticket_id as string | undefined;

  if (outcome !== 'MERGE_APPLIED' && outcome !== 'ALREADY_APPLIED') {
    const err = new Error(
      MERGE_REFUSAL_COPY[outcome ?? ''] ??
        `This merge could not be completed (${outcome ?? 'unknown outcome'}).`,
    ) as Error & { outcome?: string };
    err.outcome = outcome;
    throw err;
  }
  if (!mergedTicketId) {
    throw new Error('The merge reported success without naming a merged ticket');
  }

  // ══ 5. AFTER-COMMIT ONLY ════════════════════════════════════════════════
  // ALREADY_APPLIED emits nothing: the human act was recorded when it
  // happened, and a recovery is not a second merge.
  if (outcome === 'MERGE_APPLIED') {
    for (const sibling of siblings ?? []) {
      if (!sibling.shop_id) continue;
      await logTicketEventToOcis({
        ticketId: sibling.id,
        shopId: sibling.shop_id,
        eventType: 'ticket_status_changed',
        content: `Merged into ticket ${mergedTicketId}`,
        authorUserId: user?.id,
        payload: {
          fromStatus: sibling.status,
          toStatus: 'Deleted',
          reason: 'split_merge',
          merged_into_ticket_id: mergedTicketId,
          split_group_id: sibling.split_group_id,
          operation_id: operationId,
        },
      }).catch(err => console.error('[mergeSplitTickets] OCIS log error:', err));
    }
  }

  return mergedTicketId;
}

/**
 * What each canonical refusal means to the person who clicked Merge.
 *
 * The authority answers with a named outcome; this is the only place that
 * turns one into English, so a new refusal cannot reach an operator as a raw
 * enum or as a generic failure.
 */
const MERGE_REFUSAL_COPY: Record<string, string> = {
  ACTIVE_WORK:
    'A technician is working on part of this split. Ask them to stop before merging.',
  PAUSED_WORK:
    'Part of this split is paused. Resolve the pause before merging.',
  REVIEW_REQUIRED:
    'Time on this split is waiting to be reviewed. Finish the review before merging.',
  TIMER_INTEGRITY_BLOCK:
    'The timer records for this split disagree. That has to be resolved before merging.',
  SIBLING_NOT_MERGEABLE:
    'One part of this split has been completed, picked up, or is a rollover continuation, so it cannot be merged back.',
  EVIDENCE_BOUND_JOB_LINE:
    'Work has already been recorded against one of these job lines, so it cannot be moved to a merged ticket.',
  LOCATION_CONFLICT:
    'The parts of this split disagree about where the vehicle is. Fix the location first.',
  FAMILY_SET_MISMATCH:
    'This split changed while you were looking at it. Refresh and try again.',
  FAMILY_CHANGED:
    'This split changed while the merge was starting. Refresh and try again.',
  FAMILY_TOO_SMALL:
    'There are no longer two live parts to merge.',
  NOT_A_SPLIT_FAMILY:
    'This ticket is not part of a split.',
  TICKET_NOT_AVAILABLE:
    'This ticket is not available to merge.',
  NOT_AUTHENTICATED:
    'You are signed out. Sign in and try again.',
  MISSING_OPERATION_ID:
    'This merge attempt could not be identified. Refresh and try again.',
};

/**
 * Check if a ticket is part of a split
 */
export async function isTicketSplit(ticketId: string): Promise<boolean> {
  const { data } = await supabase
    .from('tickets')
    .select('split_group_id')
    .eq('id', ticketId)
    .single();
  
  return !!data?.split_group_id;
}
